import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CardContent,
  Fade,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeAutoObservable, runInAction } from 'mobx';
import { Api } from '../../api';
import credentialStore from '../../state/credentialStore';
import { route, SESSIONS_TABS, sessionTabRoute } from '../../utils/routing';
import { PAGES } from '../../components/main/MainComponent';
import GoogleMapsWrapper from '../../components/google/GoogleMapsWrapper';
import TextLink from '../../components/other/TextLink';

export interface ConnectorMarkerData {
  lat: number;
  lng: number;
  locationName?: string;
  locationAddress?: string;
  locationid?: string;
  chargePointIds?: string[];
  connectors: {
    totalCount: number;
    inUseCount: number;
    errorCount: number;
    maintenanceCount: number;
    hasError: boolean;
  };
}

class DashboardViewStore {
  usersCount = 0;
  locationsCount = 0;
  chargersCount = 0;
  connectorsCount = 0;

  sessionsCount = 0;
  activeSessionsCount = 0;

  publicChargersCount = 0;
  domicilChargersCount = 0;
  unassignedChargersCount = 0;
  issuesChargersCount = 0;

  availableConnectorsCount = 0;
  inUseConnectorsCount = 0;
  inMaintenanceConnectorsCount = 0;
  inOfflineConnectorsCount = 0;
  inErrorConnectorsCount = 0;

  connectorMarkers: ConnectorMarkerData[] = [];

  isLoading = true;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  resetCounts() {
    this.usersCount = 0;
    this.locationsCount = 0;
    this.chargersCount = 0;
    this.connectorsCount = 0;

    this.sessionsCount = 0;
    this.activeSessionsCount = 0;

    this.publicChargersCount = 0;
    this.domicilChargersCount = 0;
    this.unassignedChargersCount = 0;
    this.issuesChargersCount = 0;

    this.availableConnectorsCount = 0;
    this.inUseConnectorsCount = 0;
    this.inMaintenanceConnectorsCount = 0;
    this.inOfflineConnectorsCount = 0;
    this.inErrorConnectorsCount = 0;
  }

  setConnectorMarkers(markers: ConnectorMarkerData[]) {
    this.connectorMarkers = markers;
  }
}

const dashboardViewStore = new DashboardViewStore();
const defaultPosition = { lat: 46.0541709, lng: 14.5045177 };

const DashboardPage = observer(() => {
  const getPosition = () => {
    if (dashboardViewStore.connectorMarkers.length > 0) {
      const marker = dashboardViewStore.connectorMarkers[0];
      return { lat: marker.lat, lng: marker.lng };
    }

    return defaultPosition;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accountId = credentialStore.getAccountId();

        const [
          usersResp,
          locationsResp,
          statsSummaryResp,
          sessionsResp,
          activeSessionsResp,
          mapLocations,
        ] = credentialStore.isUserAdmin
          ? await Promise.all([
              Api.user.listUsers(),
              Api.charge.listLocations(),
              Api.charge.getStatsSummary(),
              Api.charge.listSessions(),
              Api.charge.listActiveSessions(),
              Api.charge.adminMapLocations(),
            ])
          : await Promise.all([
              Promise.resolve(null),
              Api.charge.listLocationsForAccount(accountId),
              Api.charge.getStatsSummary({ accountId }),
              Api.charge.listSessionsForAccount(accountId),
              Api.charge.listSessionsForAccount(accountId, { isActive: true }),
              Api.charge.accountMapLocations(accountId),
            ]);

        runInAction(() => {
          dashboardViewStore.resetCounts();

          const stats = statsSummaryResp?.data?.data;

          // Stats from various APIs
          dashboardViewStore.usersCount = usersResp?.data?.data?.count || 0;
          dashboardViewStore.locationsCount = locationsResp?.data?.count || 0;
          dashboardViewStore.sessionsCount = sessionsResp?.data?.count || 0;
          dashboardViewStore.activeSessionsCount = activeSessionsResp?.data?.count || 0;

          // Charger counts from stats API
          dashboardViewStore.chargersCount = stats?.chargerCount || 0;
          dashboardViewStore.publicChargersCount = stats?.publicChargerCount || 0;
          dashboardViewStore.domicilChargersCount = stats?.domicilChargerCount || 0;
          dashboardViewStore.issuesChargersCount = stats?.unavailableChargerCount || 0;

          // Connector counts from stats API
          dashboardViewStore.connectorsCount = stats?.connectorCount || 0;
          dashboardViewStore.availableConnectorsCount = stats?.availableConnectorCount || 0;
          dashboardViewStore.inUseConnectorsCount = stats?.inUseConnectorCount || 0;
          dashboardViewStore.inMaintenanceConnectorsCount = stats?.maintenanceConnectorCount || 0;
          dashboardViewStore.inOfflineConnectorsCount = stats?.offlineConnectorCount || 0;
          dashboardViewStore.inErrorConnectorsCount =
            (stats?.maintenanceConnectorCount || 0) + (stats?.offlineConnectorCount || 0);

          dashboardViewStore.setConnectorMarkers(
            mapLocations?.data?.data?.map((location) => {
              const { connectors } = location;
              // Filter out null values from chargePointIds
              const chargePointIds = (location.chargePointIds || []).filter((id) => id !== null);

              return {
                lat: parseFloat(location.latitude),
                lng: parseFloat(location.longitude),
                locationName: location.name,
                locationAddress: location.address,
                locationid: location.id,
                chargePointIds,
                connectors: connectors || {
                  totalCount: 0,
                  inUseCount: 0,
                  errorCount: 0,
                  maintenanceCount: 0,
                  hasError: false,
                },
              };
            }) ?? [],
          );

          dashboardViewStore.isLoading = false;
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        dashboardViewStore.isLoading = false;
      }
    };

    dashboardViewStore.isLoading = true;
    fetchData();
  }, [credentialStore.selectedAccount]);

  return (
    <div className="mainContainer" style={{ width: '100%' }}>
      <Fade in={!dashboardViewStore.isLoading} timeout={1000}>
        <div className="cardsContainer" style={{ position: 'relative', width: '100%', zIndex: 10 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={4}>
              <Accordion style={{ borderRadius: '18px' }} defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography variant="h6" style={{ fontWeight: 400, fontSize: '18px' }}>
                    Stats
                  </Typography>
                </AccordionSummary>
                <div
                  style={{ width: '100%', height: '0.1px', backgroundColor: '#ddd', margin: '0' }}
                />
                <AccordionDetails style={{ display: 'block', paddingTop: '1rem', padding: '0px' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Locations
                      </Typography>
                      <TextLink
                        to={route(PAGES.Locations)}
                        label={dashboardViewStore.locationsCount + ''}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Users
                      </Typography>
                      <TextLink
                        to={route(PAGES.Users)}
                        label={dashboardViewStore.usersCount + ' '}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Sessions
                      </Typography>
                      <TextLink
                        to={sessionTabRoute(SESSIONS_TABS.HISTORY)}
                        label={dashboardViewStore.sessionsCount + ' '}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Current Active Sessions
                      </Typography>
                      <TextLink
                        to={sessionTabRoute(SESSIONS_TABS.ACTIVE)}
                        label={dashboardViewStore.activeSessionsCount + ' '}
                      />
                    </Box>
                  </CardContent>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Accordion style={{ borderRadius: '18px' }} defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography variant="h6" style={{ fontWeight: 400, fontSize: '18px' }}>
                    Chargers
                  </Typography>
                </AccordionSummary>
                <div
                  style={{ width: '100%', height: '0.1px', backgroundColor: '#ddd', margin: '0' }}
                />
                <AccordionDetails style={{ display: 'block', paddingTop: '1rem', padding: '0px' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Public
                      </Typography>
                      <TextLink
                        to={`${route(PAGES.Chargers)}/all?type=public`}
                        label={dashboardViewStore.publicChargersCount + ' '}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Domicil
                      </Typography>
                      <TextLink
                        to={`${route(PAGES.Chargers)}/all?type=domicil`}
                        label={dashboardViewStore.domicilChargersCount + ' '}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Unavailable
                      </Typography>
                      <TextLink
                        to={`${route(PAGES.Chargers)}/unavailable`}
                        label={dashboardViewStore.issuesChargersCount + ' '}
                        color="red"
                      />
                    </Box>
                  </CardContent>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Accordion style={{ borderRadius: '18px' }} defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography variant="h6" style={{ fontWeight: 400, fontSize: '18px' }}>
                    Charge Connections
                  </Typography>
                </AccordionSummary>
                <div
                  style={{ width: '100%', height: '0.1px', backgroundColor: '#ddd', margin: '0' }}
                />
                <AccordionDetails style={{ display: 'block', paddingTop: '1rem', padding: '0px' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Available
                      </Typography>
                      <Typography variant="body1">
                        {dashboardViewStore.availableConnectorsCount}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        In use
                      </Typography>
                      <Typography variant="body1">
                        {dashboardViewStore.inUseConnectorsCount}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Issues
                      </Typography>
                      <TextLink
                        to={`${route(PAGES.Chargers)}/unavailable`}
                        label={dashboardViewStore.inErrorConnectorsCount + ' '}
                        color="red"
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Offline
                      </Typography>
                      <Typography variant="body1">
                        {dashboardViewStore.inOfflineConnectorsCount}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" style={{ color: '#757575' }}>
                        Maintenance
                      </Typography>
                      <Typography variant="body1">
                        {dashboardViewStore.inMaintenanceConnectorsCount}
                      </Typography>
                    </Box>
                  </CardContent>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </div>
      </Fade>
      <Fade in={!dashboardViewStore.isLoading} timeout={1000}>
        <div>
          {!dashboardViewStore.isLoading && (
            <GoogleMapsWrapper
              centerByMarkers={true}
              pos={getPosition()}
              connectorMarkers={dashboardViewStore.connectorMarkers}
            />
          )}
        </div>
      </Fade>
    </div>
  );
});

export default DashboardPage;
